import { isUrbanCode, normalizeShippingCode } from "./codes"
import {
  DEFAULT_NOMIN_PKEY,
  type AddressDeps,
  type CoverageDistrict,
  type SavedShippingAddress,
  type StockLocation,
} from "./types"

export type {
  AddressDeps,
  CoverageDistrict,
  CoverageKhoroo,
  SavedShippingAddress,
  StockLocation,
} from "./types"

/**
 * Lowercase, ё→е, strip дүүрэг / хороо / spaces / punctuation.
 */
export function normalizePlaceName(
  value: string | null | undefined
): string {
  if (value == null) return ""
  return String(value)
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/дүүрэг/g, "")
    .replace(/хороо/g, "")
    .replace(/[\s\p{P}\p{S}]+/gu, "")
}

function firstNumber(value: string): number | null {
  const match = String(value).match(/\d+/)
  if (!match) return null
  const n = Number(match[0])
  return Number.isFinite(n) ? n : null
}

/**
 * Match khoroo names. Prefer first-number equality (`1-р хороо` ↔ `1`).
 */
export function khorooMatches(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  const left = a ?? ""
  const right = b ?? ""
  const numA = firstNumber(left)
  const numB = firstNumber(right)
  if (numA != null && numB != null) return numA === numB

  const na = normalizePlaceName(left)
  const nb = normalizePlaceName(right)
  return na.length > 0 && na === nb
}

export function getLocationCode(
  address: Pick<SavedShippingAddress, "locationCode"> | null | undefined
): string {
  const code = address?.locationCode?.trim()
  return code || DEFAULT_NOMIN_PKEY
}

const REAL_ADDRESS_FIELDS = [
  "streetLine1",
  "streetLine2",
  "city",
  "province",
  "postalCode",
  "addressId",
] as const

/**
 * True only when a shipping session has a real address field.
 * Branch (including `"default"`) and method code do not count.
 */
export function hasRealShippingAddress(
  shipping:
    | (Partial<
        Record<(typeof REAL_ADDRESS_FIELDS)[number], string | null | undefined>
      > & { branch?: string | null })
    | null
    | undefined
): boolean {
  if (!shipping) return false
  return REAL_ADDRESS_FIELDS.some((key) => {
    const value = shipping[key]
    return typeof value === "string" && value.trim().length > 0
  })
}

export type ResolveNominPkeyArgs = {
  stockLocations: StockLocation[]
  branch?: string | null
  method?: string | null
  stockLocationId?: string | null
}

/**
 * Resolve Nomin stock-location pkey for an address / method.
 * Misses always return the ESHOP default.
 */
export function resolveNominPkey({
  stockLocations,
  branch,
  method,
  stockLocationId,
}: ResolveNominPkeyArgs): string {
  const code = normalizeShippingCode(method ?? "")

  if (code === "standard") return DEFAULT_NOMIN_PKEY

  if (stockLocationId) {
    const byId = stockLocations.find((loc) => loc.id === stockLocationId)
    const pkey = byId?.customFields?.nominPkey?.trim()
    return pkey || DEFAULT_NOMIN_PKEY
  }

  if (code === "express" || code === "night" || code === "rural") {
    const normalizedBranch = normalizePlaceName(branch)
    if (normalizedBranch) {
      const byName = stockLocations.find(
        (loc) => normalizePlaceName(loc.name) === normalizedBranch
      )
      const pkey = byName?.customFields?.nominPkey?.trim()
      if (pkey) return pkey
    }
  }

  return DEFAULT_NOMIN_PKEY
}

function copyAddress(
  source: SavedShippingAddress,
  patch: Partial<SavedShippingAddress>
): SavedShippingAddress {
  return {
    addressId: source.addressId,
    shippingMethodCode: source.shippingMethodCode,
    fullName: source.fullName,
    streetLine1: source.streetLine1,
    streetLine2: source.streetLine2,
    city: source.city,
    province: source.province,
    phoneNumber: source.phoneNumber,
    latitude: source.latitude,
    longitude: source.longitude,
    branch: source.branch,
    countryCode: source.countryCode,
    storeType: source.storeType,
    locationCode: source.locationCode,
    stockLocationId: source.stockLocationId,
    ...patch,
  }
}

export function isUbCountryCode(code: string | null | undefined): boolean {
  const normalized = String(code ?? "").trim().toLowerCase()
  return (
    normalized === "ub" ||
    normalized === "ulaanbaatar" ||
    normalized === "ulaan baatar" ||
    normalized === "mn-ub"
  )
}

export type PreparedCustomerAddress<T> = {
  address: T
  isEnabled: boolean
  stockLocationId: string | null
}

type MethodAddressLike = {
  country?: { code?: string | null } | null
  province?: string | null
  city?: string | null
}

/**
 * Addresses shown for a shipping method.
 * Rural keeps non-Ulaanbaatar addresses. Other methods keep Ulaanbaatar.
 * Express and night stay visible outside coverage, but cannot be selected.
 */
export function prepareCustomerAddressesForMethod<T extends MethodAddressLike>(
  addresses: readonly T[],
  shippingMethodCode: string,
  coverage: CoverageDistrict[] = [],
  options?: { coverageReady?: boolean }
): PreparedCustomerAddress<T>[] {
  const code = normalizeShippingCode(shippingMethodCode)
  const wantUb = code !== "rural"
  const base = addresses.filter((address) => {
    const isUb = isUbCountryCode(address.country?.code)
    return wantUb ? isUb : !isUb
  })

  const usesCoverage =
    code === "express" || code === "night" || code === "real-express"
  if (!usesCoverage || options?.coverageReady === false) {
    return base.map((address) => ({
      address,
      isEnabled: true,
      stockLocationId: null,
    }))
  }

  const covered: PreparedCustomerAddress<T>[] = []
  const uncovered: PreparedCustomerAddress<T>[] = []
  for (const address of base) {
    const stockLocationId = findCoverageStockLocationId(
      {
        province: address.province ?? "",
        city: address.city ?? "",
      } as SavedShippingAddress,
      coverage
    )
    const item = {
      address,
      isEnabled: Boolean(stockLocationId),
      stockLocationId,
    }
    if (item.isEnabled) covered.push(item)
    else uncovered.push(item)
  }
  return [...covered, ...uncovered]
}

function findCoverageStockLocationId(
  source: SavedShippingAddress,
  coverage: CoverageDistrict[]
): string | null {
  const province = source.province ?? ""
  const city = source.city ?? ""
  if (!province && !city) return null

  for (const district of coverage) {
    if (normalizePlaceName(district.district) !== normalizePlaceName(province)) {
      continue
    }
    for (const khoroo of district.khoroos) {
      if (khorooMatches(khoroo.name, city)) {
        const id = khoroo.stockLocationId?.trim()
        if (id) return id
      }
    }
  }
  return null
}

/**
 * Convert a saved address to a target shipping method.
 * Returns null when express/night cannot resolve a stock location.
 */
export function convertAddress(
  source: SavedShippingAddress,
  targetCode: string,
  { coverage, stockLocations }: AddressDeps
): SavedShippingAddress | null {
  const code = normalizeShippingCode(targetCode)
  if (!code) return null

  // rural / pickup (non-urban)
  if (code === "rural" || code === "pickup") {
    const locationCode = resolveNominPkey({
      stockLocations,
      branch: source.branch,
      method: code,
      stockLocationId: source.stockLocationId || null,
    })
    return copyAddress(source, {
      shippingMethodCode: code,
      locationCode,
      stockLocationId: source.stockLocationId || "",
    })
  }

  // standard
  if (code === "standard") {
    return copyAddress(source, {
      shippingMethodCode: code,
      stockLocationId: "",
      locationCode: DEFAULT_NOMIN_PKEY,
    })
  }

  // express / night
  if (code === "express" || code === "night") {
    let stockLocationId = findCoverageStockLocationId(source, coverage)

    if (!stockLocationId) {
      const sourceCode = normalizeShippingCode(source.shippingMethodCode)
      if (sourceCode === code && source.stockLocationId) {
        stockLocationId = source.stockLocationId
      }
    }

    if (!stockLocationId) return null

    const locationCode = resolveNominPkey({
      stockLocations,
      branch: source.branch,
      method: code,
      stockLocationId,
    })

    return copyAddress(source, {
      shippingMethodCode: code,
      stockLocationId,
      locationCode,
    })
  }

  // Unknown codes: copy with updated code, resolve pkey from branch when possible
  const locationCode = resolveNominPkey({
    stockLocations,
    branch: source.branch,
    method: code,
    stockLocationId: source.stockLocationId || null,
  })
  return copyAddress(source, {
    shippingMethodCode: code,
    locationCode,
  })
}

function donorCodesFor(code: string): string[] {
  if (code === "express") return ["express", "standard", "night"]
  if (code === "night") return ["night", "standard", "express"]
  if (code === "standard") return ["standard", "express", "night"]
  if (code === "rural") return ["rural"]
  if (code === "pickup") return ["pickup"]
  return [code]
}

/** True when a saved address exists that could be converted to `code`. */
export function hasAddressDonor(
  code: string,
  addressesByCode: Record<string, SavedShippingAddress | null | undefined>
): boolean {
  const target = normalizeShippingCode(code)
  if (!target) return false
  return donorCodesFor(target).some((donor) => Boolean(addressesByCode[donor]))
}

export type ResolveAddressResult = {
  address: SavedShippingAddress | null
  addresses: Record<string, SavedShippingAddress>
}

/**
 * Resolve a usable address for `code`, converting from donor methods when needed.
 */
export function resolveAddressForMethod(
  code: string,
  addressesByCode: Record<string, SavedShippingAddress>,
  deps: AddressDeps
): ResolveAddressResult {
  const target = normalizeShippingCode(code)
  let addresses: Record<string, SavedShippingAddress> = { ...addressesByCode }

  // 1. Exact saved address
  const exact = addresses[target]
  if (exact) {
    if (target !== "express" && target !== "night") {
      return { address: exact, addresses }
    }

    const validated = convertAddress(exact, target, deps)
    if (validated) {
      addresses = { ...addresses, [target]: validated }
      return { address: validated, addresses }
    }

    // Coverage miss — drop and continue with donors
    addresses = { ...addresses }
    delete addresses[target]
  }

  // 2–3. Donors → first successful convert
  for (const donorCode of donorCodesFor(target)) {
    const donor = addresses[donorCode]
    if (!donor) continue

    const converted = convertAddress(donor, target, deps)
    if (!converted) continue

    addresses = { ...addresses, [target]: converted }
    return { address: converted, addresses }
  }

  return { address: null, addresses }
}

/**
 * Save an address under its code and share across urban methods when applicable.
 * Failed express/night conversions delete any previously saved address for that method.
 */
export function saveAndShareAddress(
  address: SavedShippingAddress,
  addresses: Record<string, SavedShippingAddress>,
  deps: AddressDeps
): Record<string, SavedShippingAddress> {
  const code = normalizeShippingCode(address.shippingMethodCode)
  const next: Record<string, SavedShippingAddress> = {
    ...addresses,
    [code]: { ...address, shippingMethodCode: code },
  }

  if (!isUrbanCode(code)) {
    return next
  }

  for (const urban of ["standard", "express", "night"] as const) {
    if (urban === code) continue

    const converted = convertAddress(next[code], urban, deps)
    if (converted) {
      next[urban] = converted
    } else if (urban === "express" || urban === "night") {
      delete next[urban]
    }
  }

  return next
}

/** Minimal customer address shape used for login / tab-switch standard preset. */
export type CustomerAddressLike = {
  id: string
  fullName?: string | null
  streetLine1?: string | null
  streetLine2?: string | null
  city?: string | null
  province?: string | null
  phoneNumber?: string | null
  country?: { code?: string | null } | null
  defaultShippingAddress?: boolean | null
  customFields?: {
    latitude?: number | null
    longitude?: number | null
    branch?: string | null
    shippingMethodCode?: string | null
    storeType?: string | null
  } | null
}

/**
 * Pick a customer address to seed `standard`.
 * 1. defaultShippingAddress when method is empty or standard
 * 2. first address with shippingMethodCode standard
 * 3. optionally the first address (login / bootstrap only)
 */
export function pickCustomerAddressForStandardPreset(
  addresses: CustomerAddressLike[] | null | undefined,
  options?: { allowFirstAny?: boolean }
): CustomerAddressLike | null {
  if (!Array.isArray(addresses) || addresses.length === 0) return null

  const defaultAddr = addresses.find((a) => a.defaultShippingAddress)
  if (defaultAddr) {
    const code = normalizeShippingCode(
      defaultAddr.customFields?.shippingMethodCode
    )
    if (!code || code === "standard") return defaultAddr
  }

  const firstStandard = addresses.find(
    (a) =>
      normalizeShippingCode(a.customFields?.shippingMethodCode) === "standard"
  )
  if (firstStandard) return firstStandard

  if (options?.allowFirstAny) return addresses[0] ?? null
  return null
}

/** Map a Vendure customer address into a standard SavedShippingAddress. */
export function customerAddressToSavedStandard(
  address: CustomerAddressLike
): SavedShippingAddress {
  return {
    addressId: String(address.id ?? ""),
    shippingMethodCode: "standard",
    fullName: address.fullName || "",
    streetLine1: address.streetLine1 || "",
    streetLine2: address.streetLine2 || "",
    city: address.city || "",
    province: address.province || "",
    phoneNumber: address.phoneNumber || "",
    latitude: address.customFields?.latitude ?? null,
    longitude: address.customFields?.longitude ?? null,
    branch: address.customFields?.branch?.trim() || "default",
    countryCode: address.country?.code || "",
    storeType: address.customFields?.storeType || "",
    locationCode: DEFAULT_NOMIN_PKEY,
    stockLocationId: "",
  }
}
